import { z } from "zod";
import { PolicyIssue } from "./openIssue";
import { AttachmentWithId } from "./attachment";

export enum ChangeControlStatusEnum {
  NOT_IN_REVIEW = "unstaged",
  IN_PROGRESS = "staged",
  APPROVED = "approved",
  NOT_APPROVED = "not_approved",
}

export enum RequirementLevels {
  requirement1 = "req1",
  requirement2 = "req2",
  requirement3 = "req3",
}

export enum TestCaseLevels {
  testCase1 = "testCase1",
  testCase2 = "testCase2",
  testCase3 = "testCase3",
}

export const ResourceIdentifier = z.object({
  qri: z.string(),
  label: z.string(),
});
export type ResourceIdentifier = z.infer<typeof ResourceIdentifier>;

export const ChangeControlStatus = z.nativeEnum(ChangeControlStatusEnum);
export type ChangeControlStatus = z.infer<typeof ChangeControlStatus>;

export const BaseDesignElement = z.object({
  id: z.string(),
  title: z.string().min(1).max(255, "Title is too long"),
  type: z.string(),
  code: z.string(),
  itemStatus: z.string(),
  changeControlStatus: ChangeControlStatus,
  created: z.string(),
  updated: z.string(),
  source: z.string(),
  version: z.string(),
  url: z.string().optional(),
  policyIssues: z.record(PolicyIssue, z.boolean()),
});
export type BaseDesignElement = z.infer<typeof BaseDesignElement>;

export const LinkedElement = z.object({
  id: z.string(),
  title: z.string(),
  code: z.string(),
  itemStatus: z.string().optional(),
  changeControlStatus: z.string().optional(),
  created: z.string().optional(),
  updated: z.string().optional(),
  source: z.string().optional(),
  type: z.string(),
  qri: z.string().optional(),
  subType: z.string().optional(),
  version: z.string().optional(),
  category: ResourceIdentifier.optional(),
  component: ResourceIdentifier.optional(),
});
export type LinkedElement = z.infer<typeof LinkedElement>;

export const Requirement = BaseDesignElement.extend({
  description: z.string().nullish(),
  category: ResourceIdentifier.optional(),
  component: ResourceIdentifier.optional(),
  children: LinkedElement.array(),
  parent: LinkedElement.array(),
  attachments: AttachmentWithId.array(),
  version: z.string(),
  sourcedItems: LinkedElement.array(),
  mitigates: LinkedElement.array(),
});

export const Level1RequirementElement = Requirement.extend({
  type: z.literal("req1"),
});
export type Level1RequirementElement = z.infer<typeof Level1RequirementElement>;

export const Level2RequirementElement = Requirement.extend({
  type: z.literal("req2"),
});
export type Level2RequirementElement = z.infer<typeof Level2RequirementElement>;

export const Level3RequirementElement = Requirement.extend({
  type: z.literal("req3"),
});
export type Level3RequirementElement = z.infer<typeof Level3RequirementElement>;

export const Level4RequirementElement = Requirement.extend({
  type: z.literal("req4"),
});
export type Level4RequirementElement = z.infer<typeof Level4RequirementElement>;

export const RequirementDesignElement = z.discriminatedUnion("type", [
  Level1RequirementElement,
  Level2RequirementElement,
  Level3RequirementElement,
  Level4RequirementElement,
]);

export type RequirementDesignElement =
  | Level1RequirementElement
  | Level2RequirementElement
  | Level3RequirementElement
  | Level4RequirementElement;

export const TestResult = z.object({
  result: z.string().min(1, { message: "Must select a test result" }),
  executedBy: z
    .string()
    .min(1, { message: "Must enter a tester" })
    .max(255, { message: "Tester name too long at most 255 characters" }),
  date: z.string().optional(),
  notes: z
    .string()
    .max(500, { message: "Note too long at most 500 characters" })
    .nullish(),
  source: z.string().optional(),
  attachments: AttachmentWithId.array().optional(),
});
export type TestResult = z.infer<typeof TestResult>;

export enum ResultOptions {
  PASSED = "passed",
  FAILED = "failed",
  BLOCKED = "blocked",
}

export const Level1TestCaseElement = Requirement.extend({
  type: z.literal("testCase1"),
  category: ResourceIdentifier.optional(),
  testResult: TestResult.optional(),
});
export type Level1TestCaseElement = z.infer<typeof Level1TestCaseElement>;

export const Level2TestCaseElement = Requirement.extend({
  type: z.literal("testCase2"),
  category: ResourceIdentifier.optional(),
  testResult: TestResult.optional(),
});
export type Level2TestCaseElement = z.infer<typeof Level2TestCaseElement>;

export const Level3TestCaseElement = Requirement.extend({
  type: z.literal("testCase3"),
  category: ResourceIdentifier.optional(),
  testResult: TestResult.optional(),
});
export type Level3TestCaseElement = z.infer<typeof Level3TestCaseElement>;

export type TestCaseDesignElement =
  | Level1TestCaseElement
  | Level2TestCaseElement
  | Level3TestCaseElement;

export const Risk = BaseDesignElement.extend({
  type: z.literal("risk"),
  subType: z.string().optional(),
  sourceItems: LinkedElement.array(),
  mitigationItems: LinkedElement.array(),
  category: ResourceIdentifier.optional(),
}).passthrough();
export type Risk = z.infer<typeof Risk>;

export const ISOMitigation = z.object({
  probability: z.string().optional(),
  severity: z.string().optional(),
  riskLevel: z.string().optional(),
});
export type ISOMitigation = z.infer<typeof ISOMitigation>;

export const ISORiskDesignElement = Risk.extend({
  subType: z.literal("iso").optional(),
  harm: z.string().optional(),
  foreseeableUseMisuse: z.string().optional(),
  postMitigation: ISOMitigation.optional(),
  hazardSituation: z.string().optional(),
  riskControl: z
    .object({
      mitigation: z.string().optional(),
    })
    .optional(),
  hazard: z.string().optional(),
  preMitigation: ISOMitigation.optional(),
});
export type ISORiskDesignElement = z.infer<typeof ISORiskDesignElement>;

export const FMEAMitigation = z.object({
  severity: z.coerce.string().optional(),
  occurrence: z.coerce.string().optional(),
  detectability: z.coerce.string().optional(),
});
export type FMEAMitigation = z.infer<typeof FMEAMitigation>;

export const FMEARiskDesignElement = Risk.extend({
  subType: z.literal("fmea").optional(),
  failureCauses: z.string().optional(),
  failureModeEffects: z.string().optional(),
  failureMode: z.string().optional(),
  evaluationMethod: z.string().optional(),
  postMitigation: FMEAMitigation.optional(),
  riskControl: z
    .object({
      mitigation: z.string().optional(),
    })
    .optional(),
  preMitigation: FMEAMitigation,
});
export type FMEARiskDesignElement = z.infer<typeof FMEARiskDesignElement>;

// design element by id
export const DesignElement = z.discriminatedUnion("type", [
  Level1RequirementElement,
  Level2RequirementElement,
  Level3RequirementElement,
  Level4RequirementElement,
  Level1TestCaseElement,
  Level2TestCaseElement,
  Level3TestCaseElement,
  Risk,
]);

export type DesignElement = z.infer<typeof DesignElement>;

export const isDesignElementRequirement = (
  item: DesignElement | LinkedElement,
): item is RequirementDesignElement => {
  return item.type.startsWith("req");
};

export const isDesignElementTestCase = (
  item: DesignElement | LinkedElement,
): item is TestCaseDesignElement => {
  return item.type.startsWith("testCase");
};

export const isDesignElementRisk = (
  item: DesignElement | LinkedElement,
): item is Risk => {
  return item.type === "risk";
};

export const isDesignElementISORisk = (
  item: DesignElement | LinkedElement,
): item is ISORiskDesignElement => {
  return isDesignElementRisk(item) && (!item.subType || item.subType === "iso");
};

export const isDesignElementFMEARisk = (
  item: DesignElement | LinkedElement,
): item is FMEARiskDesignElement => {
  return isDesignElementRisk(item) && item.subType === "fmea";
};
